import {faker} from "@faker-js/faker"
import {
  Address,
  Application,
  BusinessForm,
  BusinessInfo,
  ESignatureInfo,
  ExistingState,
  OriginalPlanInfo,
  SignupFormReview,
  SignupFormStatus,
  SoleraInfo,
  SoleraStatus,
  SpouseParticipation,
  SpouseTrusteeInfo,
  StripePurchaseInfo,
  TrusteeInfo,
  User
} from "api/sdk"

export function generateApplicationsForUsers(users: User[]): Application[] {
  return users.map((user) => ({
    _id: faker.datatype.uuid(),
    owner: user._id,
    trustee: generateTrusteeInfo(),
    business: generateBusinessInfo(),
    existing: ExistingState.Replace,
    originalPlan: generateOriginalPlanInfo(),
    spouse: SpouseParticipation.Participating,
    spouseTrusteeInfo: generateSpouseTrusteeInfo(),
    desiredTrustName: faker.company.name() + " Trust",
    notes: faker.lorem.sentences(3),
    stripe: generateStripePurchaseInfo(),
    signature: generateESignatureInfo(),
    solera: generateSoleraInfo(),
    readyForReview: faker.datatype.boolean(),
    review: generateSignupFormReview()
  }))
}

function generateTrusteeInfo(): TrusteeInfo {
  return {
    givenName: faker.name.firstName(),
    middleName: faker.name.middleName(),
    surname: faker.name.lastName(),
    address: generateAddress(),
    phone: faker.phone.number(),
    socialSecurityNumber: `${faker.finance.pin(3)}-${faker.finance.pin(
      2
    )}-${faker.finance.pin(4)}`,
    birthday: faker.date.past(100).toISOString()
  }
}

function generateAddress(): Address {
  const twoLines = faker.datatype.boolean()

  return {
    line1: faker.address.streetAddress(!twoLines),
    line2: twoLines ? faker.address.secondaryAddress() : null,
    city: faker.address.city(),
    state: faker.address.stateAbbr(),
    country: "United States",
    postalCode: faker.address.zipCode()
  }
}

function generateBusinessInfo(): BusinessInfo {
  return {
    name: faker.company.name(),
    phone: faker.phone.number(),
    startedAt: faker.date.past(50).toISOString(),
    address: generateAddress(),
    businessForm: faker.helpers.arrayElement(Object.values(BusinessForm)),
    stateOfFormation: faker.address.stateAbbr(),
    entityIdentificationNumber: `${faker.finance.pin(2)}-${faker.finance.pin(
      7
    )}`,
    naicsCodeOrDescription: faker.datatype.boolean()
      ? faker.finance.pin(6)
      : `My company uses ${faker.company.bsAdjective()} ${faker.company.bsNoun()} to ${faker.company.bs()}`,
    fiscalYearEnd: {
      month: 12,
      day: 31
    }
  }
}

function generateSpouseTrusteeInfo(): SpouseTrusteeInfo {
  const name = faker.name.fullName()
  return {
    fullLegalName: name,
    email: faker.internet.exampleEmail(name)
  }
}

function generateOriginalPlanInfo(): OriginalPlanInfo {
  return {
    originalPlanDate: faker.date.past(20).toISOString(),
    originalTrustEin: `${faker.finance.pin(2)}-${faker.finance.pin(7)}`
  }
}

function generateStripePurchaseInfo(): StripePurchaseInfo {
  return {
    subscriptionId: faker.datatype.uuid(),
    active: true
  }
}

function generateESignatureInfo(): ESignatureInfo {
  return {
    id: faker.datatype.uuid(),
    signed: true
  }
}

function generateSignupFormReview(): SignupFormReview {
  return {
    status: SignupFormStatus.Approved,
    at: faker.date.recent(4).toISOString(),
    notes: faker.lorem.sentences(2)
  }
}

function generateSoleraInfo(): SoleraInfo {
  return {
    id: faker.datatype.uuid(),
    status: faker.helpers.arrayElement(Object.values(SoleraStatus))
  }
}
